import { useEffect, useRef, useState } from 'react'
import type { AircraftCatalog, EventPlacement, ZoneGeometry } from '../engine/types'
import type { MotionConfig } from '../data/motionConfig'
import type { FieldGraph } from '../data/fieldGraph'
import type { TaxiPlan } from '../data/taxiGraph'
import type { FuelRequest } from '../motion/fuelRequest'
import type { MovementPlan } from '../motion/movementPlan'
import { resolveAllProcedures } from '../motion/resolveProcedure'
import { buildAllPlans } from '../motion/buildMovementPlan'
import { getMotionPlanWorkerService } from '../motion/worker/MotionPlanWorkerService'

export interface MovementPlanInputs {
  placements: Record<string, EventPlacement>
  motionConfig: MotionConfig
  fieldGraph: FieldGraph | null
  catalog: AircraftCatalog
  taxiPlan: TaxiPlan | null
  zones: ZoneGeometry[]
  fuelByScheduleId: Record<string, FuelRequest>
}

/**
 * Build all movement plans, off the main thread when possible.
 *
 * The heavy work (A* taxi routing + path smoothing in `buildAllPlans`) runs in a
 * Web Worker, so editing a zone / algorithm / motion setting never blocks the
 * render thread. While a rebuild is in flight the hook keeps returning the
 * previous plans (no flash); a monotonic request guard discards stale results
 * (last-result-wins). If the worker is unavailable or errors, it falls back to
 * the synchronous build on the main thread — identical output, just blocking.
 *
 * Procedure resolution is cheap (no routing) so it stays on the main thread and
 * is passed to the worker as `procedureById`.
 *
 * NOTE: every input must keep a stable identity across renders — an input that is
 * rebuilt each render (e.g. a non-memoized array) makes this effect rebuild all
 * plans on every render, and because the worker result calls `setPlans` that
 * becomes a self-sustaining rebuild loop. Inputs are memoized at the call site.
 */
export function useMovementPlans(input: MovementPlanInputs): Record<string, MovementPlan> {
  const { placements, motionConfig, fieldGraph, catalog, taxiPlan, zones, fuelByScheduleId } = input
  const [plans, setPlans] = useState<Record<string, MovementPlan>>({})
  const reqRef = useRef(0)

  useEffect(() => {
    const myReq = ++reqRef.current
    let cancelled = false

    const procedureById = resolveAllProcedures(placements, catalog, motionConfig, zones)
    const buildInput = {
      placements,
      cfg: motionConfig,
      graph: fieldGraph,
      procedureById,
      taxiPlan,
      catalog,
      fuelByScheduleId,
    }

    const isCurrent = () => !cancelled && myReq === reqRef.current

    getMotionPlanWorkerService()
      .build(buildInput)
      .then(({ plans: result, buildMs }) => {
        if (import.meta.env.DEV) {
          console.info(`[motion] worker build: n=${Object.keys(placements).length} buildMs=${buildMs.toFixed(1)}`)
        }
        if (isCurrent()) setPlans(result)
      })
      .catch(() => {
        // Worker unavailable / errored — synchronous fallback (same output).
        if (!isCurrent()) return
        setPlans(
          buildAllPlans(placements, motionConfig, fieldGraph, procedureById, taxiPlan, catalog, fuelByScheduleId)
        )
      })

    return () => {
      cancelled = true
    }
  }, [placements, motionConfig, fieldGraph, catalog, taxiPlan, zones, fuelByScheduleId])

  return plans
}
